import { useEffect, useMemo, useState } from "react";
import type { BeachRecord } from "../services/api";
import { listBeachData, listBeachWeeks } from "../services/api";
import { getErrorAlertMessage } from "../utils/errors";

export function BeachView() {
  const [beachData, setBeachData] = useState<BeachRecord[]>([]);
  const [allWeekData, setAllWeekData] = useState<BeachRecord[]>([]); // Store all data from selected week
  const [loading, setLoading] = useState(false);
  const [loadingWeeks, setLoadingWeeks] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableWeeks, setAvailableWeeks] = useState<string[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string>("");
  const [filters, setFilters] = useState<{
    office?: string;
    role?: string;
    availability?: string;
    skill?: string;
  }>({});

  // Load available weeks on mount
  useEffect(() => {
    void loadAvailableWeeks();
  }, []);

  async function loadAvailableWeeks() {
    setLoadingWeeks(true);
    try {
      const result = await listBeachWeeks();
      setAvailableWeeks(result.weeks);

      // Auto-select and load the latest week
      if (result.weeks.length > 0) {
        const latestWeek = result.weeks[0];
        setSelectedWeek(latestWeek);
        await loadWeekData(latestWeek);
      }
    } catch (err) {
      setError(getErrorAlertMessage("load available weeks", err));
    } finally {
      setLoadingWeeks(false);
    }
  }

  async function loadWeekData(week: string) {
    if (!week) {
      setError("Please select a week date");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await listBeachData({ week });
      setAllWeekData(data.items || []);
      setBeachData(data.items || []);

      if (!data.items || data.items.length === 0) {
        setError(`No beach data found for week ${week}. Try a different week or upload data via Admin Dashboard → Beach Upload.`);
      }
    } catch (err) {
      const errorMsg = getErrorAlertMessage("load beach data", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }

  async function handleWeekChange(week: string) {
    setSelectedWeek(week);
    setFilters({}); // Clear other filters when changing week
    await loadWeekData(week);
  }

  function handleFiltersChange(nextFilters: typeof filters) {
    setFilters(nextFilters);
    applyFilters(nextFilters);
  }

  function applyFilters(activeFilters: typeof filters) {
    // Apply client-side filtering on the week data
    let filtered = [...allWeekData];

    if (activeFilters.office) {
      filtered = filtered.filter((record) =>
        record.staffing_office?.toLowerCase().includes(activeFilters.office!.toLowerCase())
      );
    }

    if (activeFilters.role) {
      filtered = filtered.filter((record) =>
        record.role?.toLowerCase().includes(activeFilters.role!.toLowerCase())
      );
    }

    if (activeFilters.availability) {
      filtered = filtered.filter((record) =>
        record.availability_status?.toLowerCase().includes(activeFilters.availability!.toLowerCase())
      );
    }

    if (activeFilters.skill) {
      filtered = filtered.filter((record) =>
        record.technical_skills?.some((s) =>
          s.skill?.toLowerCase().includes(activeFilters.skill!.toLowerCase())
        )
      );
    }

    setBeachData(filtered);
  }

  function handleClearFilters() {
    const emptyFilters = {};
    setFilters(emptyFilters);
    setBeachData(allWeekData); // Reset to show all week data
  }

  const officeOptions = useMemo(() => {
    const unique = new Set<string>();
    allWeekData.forEach((record) => {
      if (record.staffing_office) unique.add(record.staffing_office);
    });
    return Array.from(unique).sort();
  }, [allWeekData]);

  const roleOptions = useMemo(() => {
    const unique = new Set<string>();
    allWeekData.forEach((record) => {
      if (record.role) unique.add(record.role);
    });
    return Array.from(unique).sort();
  }, [allWeekData]);

  const availabilityOptions = useMemo(() => {
    const unique = new Set<string>();
    allWeekData.forEach((record) => {
      if (record.availability_status) unique.add(record.availability_status);
    });
    return Array.from(unique).sort();
  }, [allWeekData]);

  // Extract unique skills from beach records
  const skillOptions = useMemo(() => {
    const unique = new Set<string>();
    allWeekData.forEach((record) => {
      record.technical_skills?.forEach((skill) => {
        if (skill.skill) unique.add(skill.skill);
      });
    });
    return Array.from(unique).sort();
  }, [allWeekData]);

  return (
    <div className="layout single-column">
      <section className="panel">
        <div className="panel-header">
          <h2>Beach Data - Week of {selectedWeek || "..."}</h2>
          <button type="button" className="ghost" onClick={() => handleWeekChange(selectedWeek)} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div className="form-grid">
          <label className="span-2">
            Week
            <select
              value={selectedWeek}
              onChange={(event) => handleWeekChange(event.target.value)}
              disabled={loading || loadingWeeks}
            >
              {loadingWeeks && <option value="">Loading weeks...</option>}
              {!loadingWeeks && availableWeeks.length === 0 && (
                <option value="">No weeks available</option>
              )}
              {availableWeeks.map((week) => (
                <option key={week} value={week}>
                  {week}
                </option>
              ))}
            </select>
            <small className="helper-text">
              {allWeekData.length > 0
                ? `Showing ${allWeekData.length} consultants for this week. Use filters below to narrow down.`
                : "Select a week to view beach data"}
            </small>
          </label>
          <label>
            Office
            <select
              value={filters.office ?? ""}
              onChange={(event) => handleFiltersChange({ ...filters, office: event.target.value || undefined })}
            >
              <option value="">All offices</option>
              {officeOptions.map((office) => (
                <option key={office} value={office}>
                  {office}
                </option>
              ))}
            </select>
          </label>
          <label>
            Role
            <select
              value={filters.role ?? ""}
              onChange={(event) => handleFiltersChange({ ...filters, role: event.target.value || undefined })}
            >
              <option value="">All roles</option>
              {roleOptions.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
          <label>
            Availability
            <select
              value={filters.availability ?? ""}
              onChange={(event) => handleFiltersChange({ ...filters, availability: event.target.value || undefined })}
            >
              <option value="">All statuses</option>
              {availabilityOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          <label>
            Skill
            <select
              value={filters.skill ?? ""}
              onChange={(event) => handleFiltersChange({ ...filters, skill: event.target.value || undefined })}
            >
              <option value="">All skills</option>
              {skillOptions.map((skill) => (
                <option key={skill} value={skill}>
                  {skill}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-actions">
          <button type="button" className="ghost" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      </section>

      <section className="content">
        {error && <div className="alert error">{error}</div>}
        {(loading || loadingWeeks) && <div className="panel">Loading beach data…</div>}
        {!loading && !loadingWeeks && availableWeeks.length === 0 && (
          <div className="panel">
            <h3>No Beach Data Available</h3>
            <p>No weeks found in the database.</p>
            <p className="helper-text">
              <strong>To get started:</strong> Upload beach data via Admin Dashboard → Beach Upload.
            </p>
          </div>
        )}
        {!loading && !loadingWeeks && selectedWeek && allWeekData.length === 0 && (
          <div className="panel">
            <h3>No Beach Data Available</h3>
            <p>No beach data found for week {selectedWeek}.</p>
            <p className="helper-text">
              <strong>To get started:</strong> Upload beach data via Admin Dashboard → Beach Upload,
              or try selecting a different week from the dropdown above.
            </p>
          </div>
        )}
        {!loading && allWeekData.length > 0 && beachData.length === 0 && (
          <div className="panel">
            <p>No consultants match your current filters ({allWeekData.length} total for this week).</p>
            <p className="helper-text">Try adjusting or clearing your filters to see more results.</p>
          </div>
        )}
        {!loading && beachData.length > 0 && (
          <div className="panel">
            <div className="panel-header">
              <h3>Beach Consultants ({beachData.length})</h3>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Week</th>
                    <th>Office</th>
                    <th>Role</th>
                    <th>Grade</th>
                    <th>Availability</th>
                    <th>Days Unassigned</th>
                    <th>Technical Skills</th>
                  </tr>
                </thead>
                <tbody>
                  {beachData.map((record, idx) => (
                    <tr key={`${record.employee_email}-${record.week_date}-${idx}`}>
                      <td>{record.employee_name}</td>
                      <td>{record.employee_email}</td>
                      <td>{record.week_date}</td>
                      <td>{record.staffing_office}</td>
                      <td>{record.role}</td>
                      <td>{record.grade}</td>
                      <td>
                        <span className={`status-pill status-${(record.availability_status || "unknown").toLowerCase()}`}>
                          {record.availability_status || "Unknown"}
                        </span>
                      </td>
                      <td>{record.days_unassigned ?? 0}</td>
                      <td>
                        {record.technical_skills && record.technical_skills.length > 0 ? (
                          <div className="chip-row">
                            {record.technical_skills.slice(0, 5).map((skill, skillIdx) => (
                              <span key={skillIdx} className="chip" title={`Level: ${skill.level}`}>
                                {skill.skill} ({skill.level})
                              </span>
                            ))}
                            {record.technical_skills.length > 5 && (
                              <span className="chip">+{record.technical_skills.length - 5} more</span>
                            )}
                          </div>
                        ) : (
                          <span className="muted">No skills</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
